import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';

import { FallbackableImage } from '@confluence/fallbackable-image';

import {
	type Suggestion,
	type SuggestionProductKey,
	getSuggestionIconUrlFromContentUrl,
	getSuggestionProductKeyFromContentUrl,
} from './__types__/RemoteObject.ts';

const i18n = defineMessages({
	hoverCardFallbackDescription: {
		id: 'space-curation.review-content-suggestions-modal.hover-card-fallback-description',
		defaultMessage: 'Open this item in a new tab to view it in {product}.',
		description:
			'Fallback description shown in the hover card when a suggestion has no description',
	},
	openHoverCardSuggestion: {
		id: 'space-curation.review-content-suggestions-modal.open-hover-card-suggestion',
		defaultMessage: 'Open item',
		description: 'Call to action in the hover card to open the suggestion URL',
	},
	hoverCardTitleLinkAriaLabel: {
		id: 'space-curation.review-content-suggestions-modal.hover-card-title-link-aria-label',
		defaultMessage: '{title}, opens in new tab',
		description: 'Accessible label for the suggestion title link in the hover card',
	},
	productGoogleDocs: {
		id: 'space-curation.review-content-suggestions-modal.product-google-docs.no-translate',
		defaultMessage: 'Google Docs',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productGoogleSheets: {
		id: 'space-curation.review-content-suggestions-modal.product-google-sheets.no-translate',
		defaultMessage: 'Google Sheets',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productGoogleSlides: {
		id: 'space-curation.review-content-suggestions-modal.product-google-slides.no-translate',
		defaultMessage: 'Google Slides',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productGoogleDrive: {
		id: 'space-curation.review-content-suggestions-modal.product-google-drive.no-translate',
		defaultMessage: 'Google Drive',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productLoom: {
		id: 'space-curation.review-content-suggestions-modal.product-loom.no-translate',
		defaultMessage: 'Loom',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productMicrosoftTeams: {
		id: 'space-curation.review-content-suggestions-modal.product-microsoft-teams.no-translate',
		defaultMessage: 'Microsoft Teams',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productLink: {
		id: 'space-curation.review-content-suggestions-modal.product-link',
		defaultMessage: 'Link',
		description: 'Fallback product label shown in the review content suggestions hover card',
	},
	groupHoverCardItemCount: {
		id: 'space-curation.review-content-suggestions-modal.group-hover-card-item-count',
		defaultMessage: '{count, plural, one {# item} other {# items}}',
		description: 'Subtitle in the group hover card showing the number of items in the group',
	},
});

const PRODUCT_LABEL_MESSAGES: Record<SuggestionProductKey, (typeof i18n)[keyof typeof i18n]> = {
	googleDocs: i18n.productGoogleDocs,
	googleSheets: i18n.productGoogleSheets,
	googleSlides: i18n.productGoogleSlides,
	googleDrive: i18n.productGoogleDrive,
	loom: i18n.productLoom,
	microsoftTeams: i18n.productMicrosoftTeams,
	link: i18n.productLink,
};

function getProductLabelMessage(productKey: SuggestionProductKey) {
	return PRODUCT_LABEL_MESSAGES[productKey];
}

const styles = cssMap({
	hoverCard: {
		width: '340px',
		backgroundColor: token('color.background.input'),
		borderRadius: token('radius.large'),
		padding: token('space.200'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	hoverCardContent: {
		maxWidth: '100%',
		minWidth: '0%',
	},
	hoverCardTitle: {
		maxWidth: '100%',
	},
	hoverCardTitleLinkWrapper: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.050'),
		minWidth: '0%',
	},
	hoverCardTitleLinkTextWrapper: {
		overflow: 'hidden',
		minWidth: '0%',
	},
	hoverCardTitleLinkIcon: {
		display: 'flex',
		alignItems: 'center',
		marginBlockStart: token('space.025'),
		opacity: 0,
		'&:hover': {
			opacity: 1,
		},
	},
	hoverCardDescription: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		// @ts-expect-error - cssMap types do not include this vendor-prefixed property, but it is required for line clamping
		WebkitBoxOrient: 'vertical',
		color: token('color.text.subtle'),
		WebkitLineClamp: '3',
		maxWidth: '100%',
	},
	hoverCardFooter: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		marginInline: `var(--ds-space-negative-150)`,
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.100'),
		color: token('color.text.subtle'),
	},
	hoverCardIcon: {
		width: '14px',
		height: '14px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
});

export const SuggestionHoverCard = ({ suggestion }: { suggestion: Suggestion }) => {
	const { formatMessage } = useIntl();
	const productKey = getSuggestionProductKeyFromContentUrl(suggestion.url);
	const iconUrl = getSuggestionIconUrlFromContentUrl(suggestion.url);
	const product = formatMessage(getProductLabelMessage(productKey));
	const description = suggestion.description?.trim()
		? suggestion.description
		: formatMessage(i18n.hoverCardFallbackDescription, { product });

	// @ts-ignore
	return (
		<Stack space="space.150" xcss={styles.hoverCard} testId="review-content-suggestion-hover-card">
			<Flex alignItems="center" gap="space.100">
				<Box xcss={styles.hoverCardIcon} aria-hidden="true">
					{iconUrl ? (
						<FallbackableImage
							src={iconUrl}
							width="14"
							height="14"
							fallback={() => <DefaultAppIcon label="" />}
							role="presentation"
						/>
					) : (
						<DefaultAppIcon label="" color={token('color.icon.subtle')} />
					)}
				</Box>
				<Stack space="space.025" xcss={styles.hoverCardContent}>
					<Box xcss={cx(styles.hoverCardTitle, styles.hoverCardTitleLinkWrapper)}>
						<Box xcss={styles.hoverCardTitleLinkTextWrapper}>
							<Link
								href={suggestion.url}
								target="_blank"
								aria-label={formatMessage(i18n.hoverCardTitleLinkAriaLabel, {
									title: suggestion.title,
								})}
							>
								<Text size="small" weight="medium" color="color.text.accent.blue">
									{suggestion.title}
								</Text>
							</Link>
						</Box>
						<Box xcss={styles.hoverCardTitleLinkIcon} aria-hidden="true">
							<LinkExternalIcon label="" size="small" color={token('color.icon.accent.blue')} />
						</Box>
					</Box>
				</Stack>
			</Flex>
			<Box xcss={styles.hoverCardDescription}>
				<Text size="small" color="color.text.subtle">
					{description}
				</Text>
			</Box>
			<Flex alignItems="center" gap="space.100" xcss={styles.hoverCardFooter}>
				<Box xcss={styles.hoverCardIcon} aria-hidden="true">
					{iconUrl ? (
						<FallbackableImage
							src={iconUrl}
							width="14"
							height="14"
							fallback={() => <DefaultAppIcon label="" />}
							role="presentation"
						/>
					) : (
						<DefaultAppIcon label="" color={token('color.icon.subtle')} />
					)}
				</Box>
				<Text size="small" color="color.text.subtle">
					{product}
				</Text>
			</Flex>
		</Stack>
	);
};

export const GroupHoverCard = ({ title, itemCount }: { title: string; itemCount: number }) => {
	const { formatMessage } = useIntl();

	return (
		<Stack
			space="space.150"
			xcss={styles.hoverCard}
			testId="review-content-suggestion-group-hover-card"
		>
			<Flex alignItems="center" gap="space.100">
				<Box xcss={styles.hoverCardIcon} aria-hidden="true">
					<FolderClosedIcon label="" color={token('color.icon.selected')} />
				</Box>
				<Stack space="space.025" xcss={styles.hoverCardContent}>
					<Box xcss={styles.hoverCardTitle}>
						<Text size="small" weight="medium" color="color.text.accent.blue">
							{title}
						</Text>
					</Box>
					<Text size="small" color="color.text.subtle">
						{formatMessage(i18n.groupHoverCardItemCount, { count: itemCount })}
					</Text>
				</Stack>
			</Flex>
		</Stack>
	);
};
